/**
 * UpgradeSystem — tracks the player's per-run upgrade levels.
 *
 * The Space Impact design pivot brought back in-game pickups (one per wave)
 * that boost the player's basic-laser in concrete ways. Each upgrade
 * stacks up to its cap; the level resets to all-zero on game-over → restart.
 *
 * Upgrades:
 *   - MULTISHOT (1..5): fires N bullets in a straight line each shot.
 *     L1 = 1 (vanilla), L5 = 5 parallel shots.
 *   - SPREAD (0..3): adds up to 3 extra bullets at increasing angles.
 *     L0 = none, L1 = 1 bullet at ±15°, L2 = +1 at ±30°, L3 = +1 at ±45°.
 *   - FIRE_RATE (0..3): reduces the inter-shot cooldown.
 *     L0 = 220ms (baseline), L1 = 180ms, L2 = 140ms, L3 = 100ms.
 *   - PIERCING (0..3): allows the bullet to pass through extra enemies.
 *     L0 = 1 hit, L1 = 2 hits, L2 = 3 hits, L3 = 4 hits.
 *
 * Drop model: each wave drop grants one of MULTISHOT, SPREAD, FIRE_RATE, or
 * PIERCING at +1 level. The game picks the upgrade that's farthest from
 * its cap so the player always gets meaningful progression. See
 * `nextDropUpgradeId()` below.
 */
import type { UpgradeId } from "./upgradeIds";
import { ACTIVE_DIFFICULTY } from "../config/difficultyConfig";

export { UPGRADE_IDS } from "./upgradeIds";

export interface UpgradeState {
  multishot: number;   // 1..5  (1 = single shot baseline)
  spread: number;      // 0..3
  fireRate: number;    // 0..5
  piercing: number;    // 0..3
  speed: number;       // 0..3
  shotPower: number;   // 0..13
}

export const UPGRADE_CAPS = new Proxy({} as Record<UpgradeId, number>, {
  get: (_, prop: string) => {
    const id = prop as UpgradeId;
    return ACTIVE_DIFFICULTY.upgrades[id]?.maxLevel ?? 0;
  }
});

export const UPGRADE_INITIAL: UpgradeState = {
  multishot: 1,
  spread: 0,
  fireRate: 0,
  piercing: 0,
  speed: 0,
  shotPower: 0,
};

export class UpgradeSystem {
  public state: UpgradeState = { ...UPGRADE_INITIAL };

  public reset(): void {
    this.state = { ...UPGRADE_INITIAL };
  }

  /**
   * Wave-tiered upgrade drop picker.
   *
   * Prevents early game trivialization while ensuring player firepower scales
   * smoothly in lockstep with rising enemy HP (up to 10x multiplier in wave 12+).
   *
   * Tier 1 (Waves 1-3): FIRE_RATE (max L2), SPEED (max L2), SHOT_POWER (max L2).
   * Tier 2 (Waves 4-6): PIERCING (max L1), FIRE_RATE (max L3), SHOT_POWER (max L5).
   *                     Waves 6+ unlock MULTISHOT (L2) & SPREAD (L1) at rare weight (1).
   * Tier 3 (Waves 7-9): FIRE_RATE (max L4), PIERCING (max L2), SHOT_POWER (max L8),
   *                     MULTISHOT (max L3) & SPREAD (max L2) (weight 2).
   * Tier 4 (Waves 10+): Full caps unlocked for all upgrades (weight 4).
   */
  public nextDropUpgradeId(wave: number, isBoss = false): UpgradeId | null {
    if (isBoss && this.state.speed < UPGRADE_CAPS.SPEED) {
      return "SPEED";
    }

    // Determine max allowed level per upgrade for the current wave tier
    let maxFireRate = 2;
    let maxPiercing = 0;
    let maxSpeed = 2;
    let maxShotPower = 2;
    let maxMultishot = 1;
    let maxSpread = 0;

    if (wave >= 10) {
      maxFireRate = UPGRADE_CAPS.FIRE_RATE;
      maxPiercing = UPGRADE_CAPS.PIERCING;
      maxSpeed = UPGRADE_CAPS.SPEED;
      maxShotPower = UPGRADE_CAPS.SHOT_POWER;
      maxMultishot = UPGRADE_CAPS.MULTISHOT;
      maxSpread = UPGRADE_CAPS.SPREAD;
    } else if (wave >= 7) {
      maxFireRate = 4;
      maxPiercing = 2;
      maxSpeed = 3;
      maxShotPower = 8;
      maxMultishot = 1;
      maxSpread = 0;
    } else if (wave >= 4) {
      maxFireRate = 3;
      maxPiercing = 1;
      maxSpeed = 3;
      maxShotPower = 5;
      maxMultishot = 1;
      maxSpread = 0;
    }

    const available: UpgradeId[] = [];
    const checkAdd = (id: UpgradeId, currentLevel: number, tierCap: number, weight: number) => {
      if (currentLevel < tierCap && currentLevel < UPGRADE_CAPS[id]) {
        for (let i = 0; i < weight; i++) available.push(id);
      }
    };

    const baseWeight = 4;
    checkAdd("FIRE_RATE", this.state.fireRate, maxFireRate, baseWeight);
    checkAdd("PIERCING", this.state.piercing, maxPiercing, baseWeight);
    checkAdd("SPEED", this.state.speed, maxSpeed, baseWeight);
    checkAdd("SHOT_POWER", this.state.shotPower, maxShotPower, baseWeight);

    let specialWeight = 0;
    if (wave >= 10) specialWeight = 4; // MULTISHOT and SPREAD unlock exclusively at Wave 10+

    if (specialWeight > 0) {
      checkAdd("MULTISHOT", this.state.multishot, maxMultishot, specialWeight);
      checkAdd("SPREAD", this.state.spread, maxSpread, specialWeight);
    }

    if (available.length === 0) return null;
    return available[Math.floor(Math.random() * available.length)];
  }

  /** Apply a pickup. Returns the new level of the upgrade. */
  public apply(id: UpgradeId): number {
    let field = id.toLowerCase() as keyof UpgradeState;
    if (id === "FIRE_RATE") field = "fireRate";
    if (id === "PIERCING") field = "piercing";
    if (id === "SHOT_POWER") field = "shotPower";

    const cur = (this.state as unknown as Record<string, number>)[field] as number;
    const cap = UPGRADE_CAPS[id];
    const next = Math.min(cur + 1, cap);
    (this.state as unknown as Record<string, number>)[field] = next;
    return next;
  }

  public fireCooldownMs(): number {
    const levels = ACTIVE_DIFFICULTY.upgrades.FIRE_RATE.cooldownsMs;
    return levels[Math.min(this.state.fireRate, levels.length - 1)] ?? 600;
  }

  public piercingHits(): number {
    return 1 + this.state.piercing * ACTIVE_DIFFICULTY.upgrades.PIERCING.extraHitsPerLevel;
  }

  public movementSpeed(): number {
    const levels = ACTIVE_DIFFICULTY.upgrades.SPEED.speedMultipliers;
    return levels[Math.min(this.state.speed, levels.length - 1)] ?? 1.0;
  }

  /** Bullet damage multiplier — scales with SHOT_POWER upgrade. */
  public shotDamage(): number {
    return 1 + this.state.shotPower * ACTIVE_DIFFICULTY.upgrades.SHOT_POWER.damageBonusPerLevel;
  }

  /** Total upgrade count above baseline. */
  public countTotalUpgrades(): number {
    return (
      (this.state.multishot - UPGRADE_INITIAL.multishot) +
      (this.state.spread - UPGRADE_INITIAL.spread) +
      (this.state.fireRate - UPGRADE_INITIAL.fireRate) +
      (this.state.piercing - UPGRADE_INITIAL.piercing) +
      (this.state.speed - UPGRADE_INITIAL.speed) +
      (this.state.shotPower - UPGRADE_INITIAL.shotPower)
    );
  }

  /** Minimum upgrade floor = CurrentBossesDefeated * multiplier (max 15). */
  public minimumUpgradeFloor(bossesDefeated: number): number {
    return Math.min(15, Math.max(0, Math.floor(bossesDefeated * ACTIVE_DIFFICULTY.upgradeFloorMultiplier)));
  }

  /**
   * Degrade a random active upgrade (level > baseline).
   * Respects MinimumUpgradeFloor (CurrentBossesDefeated) to prevent early-game death spirals.
   * Returns the degraded UpgradeId or null.
   */
  public degradeRandom(bossesDefeated = 0): { id: UpgradeId; newLevel: number } | null {
    const totalCount = this.countTotalUpgrades();
    const floor = this.minimumUpgradeFloor(bossesDefeated);

    // Anti-death spiral check: if current upgrades <= floor, do not degrade!
    if (totalCount <= floor) {
      return null;
    }

    const active: Array<{ id: UpgradeId; field: keyof UpgradeState; base: number }> = [
      { id: "MULTISHOT", field: "multishot", base: UPGRADE_INITIAL.multishot },
      { id: "SPREAD", field: "spread", base: UPGRADE_INITIAL.spread },
      { id: "FIRE_RATE", field: "fireRate", base: UPGRADE_INITIAL.fireRate },
      { id: "PIERCING", field: "piercing", base: UPGRADE_INITIAL.piercing },
      { id: "SPEED", field: "speed", base: UPGRADE_INITIAL.speed },
      { id: "SHOT_POWER", field: "shotPower", base: UPGRADE_INITIAL.shotPower },
    ];

    const eligible = active.filter((u) => (this.state[u.field] as number) > u.base);
    if (eligible.length === 0) return null;

    const picked = eligible[Math.floor(Math.random() * eligible.length)];
    const current = this.state[picked.field] as number;
    const next = current - 1;
    (this.state as unknown as Record<string, number>)[picked.field] = next;
    return { id: picked.id, newLevel: next };
  }
}